import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import useVehicleSingle from "../../data/useVehicleSingle";
import VehiclePartsCard from "../../components/vehicles/VehiclePartsCard";
import VehicleNotes from "../../components/leads/LeadInformation/VehicleNotes";
import AwaitingInventoryStatusDropdown from "../../components/awaitingInventory/AwaitingInventoryStatusDropdown";
import VehicleLocationDropdown from "../../components/vehicles/VehicleLocationDropdown";
import VehicleComplexityDropdown from "../../components/vehicles/VehicleComplexityDropdown";
import YardStatusDropdown from "../../components/awaitingInventory/YardStatusDropdown";
import PartsPriorityDropdown from "../../components/awaitingInventory/PartsPriorityDropdown";
import VehicleJobs from "../../components/vehicles/VehicleJobs";
import CreateJobs from "../../components/jobs/CreateJobs";
import LeadNotes from "../../components/leads/LeadInformation/LeadNotes";
import Tabs from "../../components/shared/Tabs/Tabs";
import LeadDocuments from "../../components/leads/LeadDocuments";
import PartVehicleImages from "../../components/leads/LeadParts/LeadImagesCarousel";
import VehicleWorkflowCard from "../../components/vehicles/VehicleWorkflowCard";
import LinkedWorkOrdersCard from "../../components/workOrders/LinkedWorkOrdersCard";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

export default function Vehicle() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { vehicle, lead } = useVehicleSingle(vehicleId);
  const { softDelete } = useVehiclesMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Vehicle");
  const createJobsTargetId = `vehicle-create-jobs-${vehicleId}`;

  const [viewType, setViewType] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const viewTypes = [
    { value: 0, label: "Vehicle Information" },
    { value: 1, label: `Documents` },
  ];


  useEffect(() => {
    if (!vehicle.data?.data && !vehicle.isLoading) {
      setInfo({
        name: "Vehicle not found",
        desc: "The vehicle you are looking for does not exist",
      });
      return;
    }

    if (!vehicle.data?.data && vehicle.isLoading) {
      setInfo({
        name: "",
        desc: "",
      });
      return;
    }

    setInfo({
      name: `${vehicle.data?.data?.vrm} ${vehicle.data?.data?.combinedMake} ${vehicle.data?.data?.modelRangeDescription}`,
      desc: `View and manage ${vehicle.data?.data?.vrm}`,
    });
  }, [vehicleId, vehicle.isLoading]);


  if (vehicle.isLoading || !vehicle.data?.data) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  const handleDeleteVehicle = () => {
    if (!vehicleId) return;

    softDelete.mutate(vehicleId, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        navigate("/vehicles");
      },
      onError: () => {
        deleteHandlers.onError();
      },
    });
  };



  return (
    <>
      <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

      {
        {
          0: (
            <div className="mt-8 flex flex-col gap-4">

              <div className="flex text-sm text-gray-700 gap-x-4">
                <div className="flex flex-col">
                  <p>Parts Status</p>
                  <AwaitingInventoryStatusDropdown
                    vehicle={vehicle.data.data!}
                    classes="pr-20"
                  />
                </div>
                <div className="flex flex-col">
                  <p>Parts Priority Status</p>
                  <PartsPriorityDropdown vehicle={vehicle.data.data!} classes="pr-32" />
                </div>
                <div className="flex flex-col">
                  <p>Yard Location</p>
                  <VehicleLocationDropdown vehicle={vehicle.data.data!} classes="!mt-0" size="sm" />
                </div>
                <div className="flex flex-col">
                  <p>Yard Status</p>
                  <YardStatusDropdown vehicle={vehicle.data.data!} classes="" />
                </div>
                <div className="flex flex-col">
                  <p>Complexity</p>
                  <VehicleComplexityDropdown vehicle={vehicle.data.data!} classes="w-44" />
                </div>
              </div>
              
              <div className="lg:grid lg:grid-cols-3 gap-4">
                <VehicleNotes vehicle={vehicle.data?.data} />
                {!lead.isLoading && lead.data?.data && <LeadNotes lead={lead.data.data!} notesProp="yardNotes" title="Yard Notes" />}
                <LinkedWorkOrdersCard
                  linkedItems={[
                    {
                      id: vehicle.data.data.id!,
                      name: `${vehicle.data.data.vrm ?? "Unknown VRM"}${vehicle.data.data.combinedMake ? ` - ${vehicle.data.data.combinedMake}` : ""}${vehicle.data.data.combinedModel ? ` ${vehicle.data.data.combinedModel}` : ""}`,
                      type: "Vehicle",
                    },
                  ]}
                />
              </div>

              <PartVehicleDetails vehicle={vehicle.data?.data!} price={null} collectionQuestions />

              <VehicleWorkflowCard
                vehicle={vehicle.data?.data!}
                createJobsTargetId={createJobsTargetId}
              />

              {lead.data?.data && <PartVehicleImages lead={lead.data.data!} />}

              <VehicleJobs vehicle={vehicle.data?.data} />

              <VehiclePartsCard vehicle={vehicle.data?.data!} />

              <div id={createJobsTargetId}>
                <CreateJobs lead={null} vehicle={vehicle.data?.data!} />
              </div>

              <div className="mt-6 flex justify-end">
                <ButtonInput
                  label="Delete Vehicle"
                  isSubmit={false}
                  onClick={() => setDeleteOpen(true)}
                  classes="!bg-red-600 hover:!bg-red-700"
                />
              </div>
            </div>
          ),
          1: <LeadDocuments lead={lead.data?.data!} droppedFiles={undefined} setDroppedFiles={undefined} />,

        }[viewType]
      }
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDeleteVehicle}
        isLoading={softDelete.isLoading}
        title="Delete this vehicle?"
        message="This will soft-delete the vehicle. It will not appear in normal vehicle results unless deleted filters are explicitly included."
        confirmButtonText="Delete Vehicle"
      />
    </>
  );
}
